import { useId, useState, type ReactNode } from "react";
import { ErrorApi, type Paciente } from "../api.ts";
import { cedulaValida, celularValido, correoValido, edad, telefonoValido } from "../../../dominio/src/index.ts";
import { FechaInput } from "../componentes/CampoFecha.tsx";

export function hoy(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function Logo() {
  return (
    <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 5c-3 0-5 2.5-5 6.5 0 5 2 7 3 11 .7 3 1.5 5.5 3 5.5s2-3 2.5-6c.4-2 1.5-3 3.5-3s3.1 1 3.5 3c.5 3 1 6 2.5 6s2.3-2.5 3-5.5c1-4 3-6 3-11C28 7.5 26 5 23 5c-2.5 0-4 1.5-7 1.5S11.5 5 9 5z" />
    </svg>
  );
}

/** Muestra un error de la API con sus detalles de validación. */
export function AvisoError({ error }: { error: unknown }) {
  if (!error) return null;
  const e = error instanceof ErrorApi ? error : null;
  // los errores que detecta la propia pantalla (archivo muy pesado, Excel dañado…) traen su mensaje en español: se muestran tal cual
  const mensaje = e?.message || (error instanceof Error && error.message ? error.message : "Ocurrió un error.");
  return (
    <div className="aviso error" role="alert">
      {mensaje}
      {e && e.detalles.length > 0 && <ul>{e.detalles.map((d) => <li key={d}>{d}</li>)}</ul>}
    </div>
  );
}

export type DatosPaciente = Omit<Paciente, "id" | "hc">;
export const pacienteVacio = (): DatosPaciente => ({
  nombres: "", apellidos: "", doc_tipo: "cedula", doc: "", nacimiento: null, sexo: "F", telefono: "", email: "", direccion: "", ciudad: "", filiacion: {}, representante: null,
});

/** Avisos inmediatos en la pantalla; el servidor vuelve a validar todo. */
export function problemasPaciente(p: DatosPaciente): string[] {
  const e: string[] = [];
  if (!p.nombres.trim()) e.push("Faltan los nombres.");
  if (p.doc_tipo === "cedula" && p.doc && !cedulaValida(p.doc)) e.push("La cédula no es válida.");
  if (p.telefono && !celularValido(p.telefono)) e.push("El celular debe ser 09XXXXXXXX o, si es de otro país, empezar con + y el código del país.");
  if (p.email && !correoValido(p.email)) e.push("El correo no es válido.");
  if (p.filiacion.emergencia_telefono && !telefonoValido(String(p.filiacion.emergencia_telefono))) e.push("El teléfono de emergencia debe ser un celular (09XXXXXXXX), un fijo con su código (02XXXXXXX) o un número de otro país con + y su código.");
  return e;
}

export function FormPaciente({ valor, cambiar, soloLectura = false }: { valor: DatosPaciente; cambiar: (v: DatosPaciente) => void; soloLectura?: boolean }) {
  const set = <K extends keyof DatosPaciente>(k: K, v: DatosPaciente[K]) => cambiar({ ...valor, [k]: v });
  const fil = (k: string) => String(valor.filiacion[k] ?? "");
  const setFil = (k: string, v: string) => set("filiacion", { ...valor.filiacion, [k]: v });
  const rep = valor.representante ?? {};
  const setRep = (k: string, v: string) => set("representante", { ...rep, [k]: v });
  const e = valor.nacimiento ? edad(valor.nacimiento, hoy()) : null;
  return (
    <fieldset disabled={soloLectura} style={{ border: 0, padding: 0, margin: 0, display: "grid", gap: 12 }}>
      <div className="rejilla">
        <label className="campo">Nombres *<input value={valor.nombres} onChange={(x) => set("nombres", x.target.value)} required /></label>
        <label className="campo">Apellidos<input value={valor.apellidos} onChange={(x) => set("apellidos", x.target.value)} /></label>
        <label className="campo">Documento
          <select value={valor.doc_tipo} onChange={(x) => set("doc_tipo", x.target.value as DatosPaciente["doc_tipo"])}><option value="cedula">Cédula</option><option value="pasaporte">Pasaporte</option></select>
        </label>
        <label className="campo">Número<input className="mono" inputMode={valor.doc_tipo === "cedula" ? "numeric" : "text"} value={valor.doc} onChange={(x) => set("doc", x.target.value.trim())} /></label>
        <label className="campo">Fecha de nacimiento{e !== null && ` · ${e} años`}<FechaInput max={hoy()} value={valor.nacimiento ?? ""} onChange={(x) => set("nacimiento", x.target.value || null)} /></label>
        <label className="campo">Sexo
          <select value={valor.sexo} onChange={(x) => set("sexo", x.target.value as DatosPaciente["sexo"])}><option value="F">Femenino</option><option value="M">Masculino</option><option value="O">Otro</option></select>
        </label>
        <label className="campo">Celular<input className="mono" inputMode="tel" placeholder="09XXXXXXXX o +código del país" value={valor.telefono} onChange={(x) => set("telefono", x.target.value)} /></label>
        <label className="campo">Correo<input type="email" value={valor.email} onChange={(x) => set("email", x.target.value)} /></label>
        <label className="campo">Ciudad<input value={valor.ciudad} onChange={(x) => set("ciudad", x.target.value)} /></label>
        <label className="campo">Dirección<input value={valor.direccion} onChange={(x) => set("direccion", x.target.value)} /></label>
        <label className="campo">Ocupación<input value={fil("ocupacion")} onChange={(x) => setFil("ocupacion", x.target.value)} /></label>
        <label className="campo">¿Quién lo refirió a la clínica?<input maxLength={120} placeholder="Persona, paciente o profesional que lo recomendó" value={fil("referido_por")} onChange={(x) => setFil("referido_por", x.target.value)} /></label>
        <label className="campo">Contacto de emergencia<input placeholder="Nombre y parentesco" value={fil("emergencia")} onChange={(x) => setFil("emergencia", x.target.value)} /></label>
        <label className="campo">Teléfono de emergencia<input className="mono" inputMode="tel" placeholder="09XXXXXXXX, 02XXXXXXX o +código del país" value={fil("emergencia_telefono")} onChange={(x) => setFil("emergencia_telefono", x.target.value.replace(/[^\d+ ]/g, ""))} /></label>
      </div>
      <label className="firmar"><input type="checkbox" checked={valor.filiacion.acepta_promociones !== false} onChange={(x) => set("filiacion", { ...valor.filiacion, acepta_promociones: x.target.checked })} />
        Acepta recibir felicitaciones y promociones por WhatsApp o correo (los recordatorios de citas se envían siempre).</label>
      {e !== null && e < 18 && (
        <div className="card" style={{ background: "var(--amber-soft)", boxShadow: "none" }}>
          <h3>Representante legal (paciente menor de edad)</h3>
          <div className="rejilla">
            <label className="campo">Nombre completo *<input value={rep.nombre ?? ""} onChange={(x) => setRep("nombre", x.target.value)} /></label>
            <label className="campo">Parentesco<input value={rep.parentesco ?? ""} onChange={(x) => setRep("parentesco", x.target.value)} /></label>
            <label className="campo">Cédula<input className="mono" value={rep.doc ?? ""} onChange={(x) => setRep("doc", x.target.value.trim())} /></label>
            <label className="campo">Celular<input className="mono" value={rep.telefono ?? ""} onChange={(x) => setRep("telefono", x.target.value)} /></label>
          </div>
        </div>
      )}
    </fieldset>
  );
}

export function Seccion({ titulo, children, acciones }: { titulo: string; children: ReactNode; acciones?: ReactNode }) {
  return (
    <section className="card" aria-label={titulo}>
      <div className="fila" style={{ justifyContent: "space-between" }}><h2>{titulo}</h2>{acciones}</div>
      {children}
    </section>
  );
}

export function useAccion() {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function correr<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setOcupado(true); setError(null);
    try { return await fn(); } catch (e) { setError(e); return undefined; } finally { setOcupado(false); }
  }
  return { ocupado, error, setError, correr };
}

const OJO = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
const OJO_TACHADO = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 3l18 18M10.6 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.6 6.6A17.4 17.4 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>;

/**
 * Campo de contraseña con el botón del ojo para mostrarla u ocultarla. El botón queda fuera de la etiqueta,
 * así el nombre del campo sigue siendo solo su etiqueta (lectores de pantalla y autocompletado).
 */
export function CampoClave({ etiqueta, value, onChange, autoComplete = "current-password", required, className = "entrada", placeholder }: {
  etiqueta: ReactNode; value: string; onChange: (v: string) => void; autoComplete?: string; required?: boolean; className?: string; placeholder?: string;
}) {
  const id = useId();
  const [ver, setVer] = useState(false);
  return (
    <div className="campo campo-clave">
      <label htmlFor={id}>{etiqueta}</label>
      <div className="clave-caja">
        <input id={id} className={className} type={ver ? "text" : "password"} autoComplete={autoComplete} required={required} placeholder={placeholder}
          value={value} onChange={(e) => onChange(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" />
        <button type="button" className="ver-clave" onClick={() => setVer(!ver)} aria-pressed={ver}
          aria-label={ver ? "Ocultar contraseña" : "Mostrar contraseña"} title={ver ? "Ocultar contraseña" : "Mostrar contraseña"}>{ver ? OJO_TACHADO : OJO}</button>
      </div>
    </div>
  );
}
